import { useEffect, useRef, useState } from 'react';

import { FormFeedback } from '@morressier/alembic';

import {
  Box,
  Input,
  Text,
  useOutsideClick,
  VStack,
  Flex,
  InputGroup,
  InputLeftElement,
  InputProps
} from '@chakra-ui/react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';

type Option = {
  value: string;
  label: string;
};

interface FilterableSelectProps extends Omit<InputProps, 'value' | 'onChange'> {
  options: Option[];
  name?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  value?: string;
  onValueChange?: (v: string) => void;
  error?: string;
  noResultsMessage?: string;
  /** Disables the trigger so the dropdown cannot be opened or changed. */
  isDisabled?: boolean;
}

export const FilterableSelect = ({
  options,
  name = 'select-input-search',
  placeholder = 'Select option',
  searchPlaceholder,
  value,
  onValueChange,
  error,
  noResultsMessage = 'No results found',
  isDisabled = false,
  ...inputProps
}: FilterableSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const dropdownRef = useRef(null);
  useOutsideClick({
    ref: dropdownRef,
    handler: () => setIsOpen(false)
  });

  // Collapse the dropdown if the control becomes disabled while open
  useEffect(() => {
    if (isDisabled) setIsOpen(false);
  }, [isDisabled]);

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(query.toLowerCase())
  );

  const selectedLabel =
    options.find(o => o.value === value)?.label || placeholder;

  return (
    <Box w="100%" ref={dropdownRef} position="relative">
      {/* Display Field */}
      <Flex
        tabIndex={isDisabled ? -1 : 0}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-disabled={isDisabled}
        border="1px solid"
        borderColor={error ? 'red.500' : 'inputBorder'}
        rounded="md"
        p={2}
        align="center"
        justify="space-between"
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        opacity={isDisabled ? 0.6 : 1}
        onClick={() => {
          if (!isDisabled) setIsOpen(!isOpen);
        }}
        _focus={
          isDisabled
            ? undefined
            : {
                outlineOffset: '2px',
                outlineStyle: 'solid',
                outlineColor: 'attentionSecondaryFill',
                outlineWidth: '2px'
              }
        }
        _focusVisible={
          isDisabled
            ? undefined
            : {
                outlineOffset: '2px',
                outlineStyle: 'solid',
                outlineColor: 'attentionSecondaryFill',
                outlineWidth: '2px'
              }
        }
        backgroundColor={"white"}
      >
        <Text color={'black'}>{selectedLabel}</Text>
        <FontAwesomeIcon icon={faChevronDown} />
      </Flex>

      {/* Dropdown */}
      {isOpen && !isDisabled && (
        <Box
          mt={1}
          border="1px solid"
          borderColor="gray.100"
          rounded="md"
          boxShadow="md"
          bg="white"
          position="absolute"
          w="100%"
          zIndex={50}
          maxH="280px"
          overflow="hidden"
          padding={2}
        >
          {/* Search input with icon */}
          <InputGroup
            display="flex"
            alignItems="center"
            border="1px solid"
            borderColor="inputBorder"
            rounded="md"
            mb={2}
          >
            <InputLeftElement pointerEvents="none">
              <FontAwesomeIcon icon={faMagnifyingGlass} color="#555" />
            </InputLeftElement>

            <Input
              placeholder={searchPlaceholder ?? placeholder}
              value={query}
              onChange={e => setQuery(e.target.value)}
              pl="35px"
              border="none"
              name={name}
              _focus={{ boxShadow: 'none' }}
              {...inputProps}
            />
          </InputGroup>

          <VStack align="stretch" maxH="200px" overflowY="auto" spacing={0}>
            {filtered.map(opt => {
              const isSelected = opt.value === value;

              return (
                <Box
                  key={opt.value}
                  p={2}
                  bg={isSelected ? 'gray.100' : 'transparent'}
                  fontWeight={isSelected ? 'semibold' : 'normal'}
                  _hover={{ bg: 'gray.100' }}
                  cursor="pointer"
                  onClick={() => {
                    onValueChange?.(opt.value);
                    setIsOpen(false);
                  }}
                >
                  {opt.label}
                </Box>
              );
            })}

            {filtered.length === 0 && (
              <Text p={3} color="gray.500">
                {noResultsMessage}
              </Text>
            )}
          </VStack>
        </Box>
      )}

      {error && <FormFeedback variant="error">{error}</FormFeedback>}
    </Box>
  );
};
